import { procurementApi } from "@/main/api";
import { PartsCancelledSearchRequest } from "@/main/models/request";
import { PartsCancelledSearchResponse } from "@/main/models/response";
import { getTranslateByKey } from "@/main/services/utils";
import { useCallback } from "react";

import KogCheckbox from "@/main/components/shared/checkbox/kogCheckbox";
import KOGBoxGrid from "@/main/components/shared/kogBoxGrid/kogBoxGrid";
import KOGPaginationGrid from "@/main/components/shared/paginationGrid/paginationGrid";
import RowCancelledPart from "@/main/screens/order/cancelledParts/components/cancelledPartsGrid/rowCancelledPart";

import * as StylesShared from "@/main/components/shared/styles";
import * as S from "./styles";
import { ResolutionsEnum } from "@/main/services/enum/ResolutionsEnum";

const ID_SCROLL_TARGET = "#id-general-container-screen";

type ICancelledPartsGrid = {
	filter: PartsCancelledSearchRequest | null;
	selectedParts: Set<string>;
	dataGrid: PartsCancelledSearchResponse[];
	setDataGrid: React.Dispatch<React.SetStateAction<PartsCancelledSearchResponse[]>>;
	handleChangeSelection: (guid: string) => void;
	handleSelectAll: (select: boolean) => void;
	maxSelect: number;
};

export default function CancelledPartsGrid({
	filter,
	dataGrid,
	selectedParts,
	setDataGrid,
	handleChangeSelection,
	handleSelectAll,
	maxSelect,
}: Readonly<ICancelledPartsGrid>) {
	const selectedMax = maxSelect === selectedParts.size;

	const allSelected =
		selectedParts.size > 0 &&
		dataGrid.slice(0, maxSelect).every((item) => selectedParts.has(item.procurementSupplierItemGuid));

	const isIndeterminate = selectedParts.size > 0 && !allSelected;

	const rowHeaders = [
		<StylesShared.RowHeaderTextCenter key={1}>
			<KogCheckbox
				tooltipMessage={getTranslateByKey("information.selectFirstXParts").replace("{0}", String(maxSelect))}
				checked={allSelected}
				indeterminate={isIndeterminate}
				onChange={() => handleSelectAll(!allSelected)}
				id="id-searchPartsCancelled-checkbox-selectAll"
			/>
		</StylesShared.RowHeaderTextCenter>,
		<StylesShared.TextEllipsis key={2} title={getTranslateByKey("information.claimNumber")}>
			{getTranslateByKey("information.claimNumber")}
		</StylesShared.TextEllipsis>,
		<StylesShared.TextEllipsis
			key={3}
			title={`${getTranslateByKey("information.order")}/${getTranslateByKey("information.quote")}`}
		>
			{`${getTranslateByKey("information.order")}/${getTranslateByKey("information.quote")}`}
		</StylesShared.TextEllipsis>,
		<StylesShared.TextEllipsis key={4} title={getTranslateByKey("information.vehicle")}>
			{getTranslateByKey("information.vehicle")}
		</StylesShared.TextEllipsis>,
		<StylesShared.TextEllipsis key={5} title={getTranslateByKey("information.insurer")}>
			{getTranslateByKey("information.insurer")}
		</StylesShared.TextEllipsis>,
		<StylesShared.TextEllipsis key={6} title={getTranslateByKey("information.bodyshop")}>
			{getTranslateByKey("information.bodyshop")}
		</StylesShared.TextEllipsis>,
		<StylesShared.TextEllipsis key={7} title={getTranslateByKey("information.supplier")}>
			{getTranslateByKey("information.supplier")}
		</StylesShared.TextEllipsis>,
		<StylesShared.TextEllipsis key={8} title={getTranslateByKey("information.part")}>
			{getTranslateByKey("information.part")}
		</StylesShared.TextEllipsis>,
		<StylesShared.RowHeaderTextCenter key={9}>
			<StylesShared.TextEllipsis title={getTranslateByKey("information.cancellationDate")}>
				{getTranslateByKey("information.cancellationDate")}
			</StylesShared.TextEllipsis>
		</StylesShared.RowHeaderTextCenter>,
		<StylesShared.RowHeaderTextEnd key={10}>
			<StylesShared.TextEllipsis title={getTranslateByKey("information.price")}>
				{getTranslateByKey("information.price")}
			</StylesShared.TextEllipsis>
		</StylesShared.RowHeaderTextEnd>,
	];

	const fetchData = useCallback(async (request: any) => {
		const response = await procurementApi.getPartsCancelled(request);
		return response.data;
	}, []);

	return (
		<S.Container>
			<KOGPaginationGrid
				showTitleResults
				dataGrid={dataGrid}
				formFilters={filter}
				setDataGrid={setDataGrid}
				fetchEqualFilters
				fetchMore={fetchData}
				idScrollableTarget={ID_SCROLL_TARGET}
			>
				<KOGBoxGrid
					rowHeaders={rowHeaders}
					breakpointTable={ResolutionsEnum.desktop}
					sizeColumns={["5%", "10%", "10%", "10%", "8%", "10%", "9%", "17%", "8%", "7%"]}
				>
					{dataGrid.map((part, index) => (
						<RowCancelledPart
							key={part.procurementSupplierItemGuid}
							part={part}
							handleChangeSelection={handleChangeSelection}
							isSelected={selectedParts.has(part.procurementSupplierItemGuid)}
							maxSelect={maxSelect}
							disableSelect={selectedMax && !selectedParts.has(part.procurementSupplierItemGuid)}
							index={index}
						/>
					))}
				</KOGBoxGrid>
			</KOGPaginationGrid>
		</S.Container>
	);
}
